import React, { useState } from "react";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import {
	CancelSubscriptionApi,
	SubscriptionApi,
	SubscriptionsApi,
	UpdateSubscriptionApi,
	UpdateSubscriptionsApi,
} from "../../../../../../../../../services/Api/Subscription/subscriptionTypes";
import { PriceConfiguratorContentApi } from "../../../../../../../../../services/Api/types";
import CancelSubscriptionModal from "../../CancelSubscriptionModal";
import { isArchivePlan } from "../services";
import ActiveSubscriptionForm from "./../ActiveSubscriptionForm";
import CancelledSubscription from "./../CancelledSubscription";
import PausedSubscription from "./../PausedSubscription";

interface Props {
	updateSubscription: UpdateSubscriptionApi;
	original: SubscriptionApi;
	estimate?: SubscriptionApi;
	config: PriceConfiguratorContentApi;
	index: number;
	form: UseFormReturn<UpdateSubscriptionsApi>;
	handleRemoveSubscription: (index: number) => void;
	handleUpdateSubscription: (index: number, value: UpdateSubscriptionApi) => void;
	createDefaultSubscription: (
		config: PriceConfiguratorContentApi,
		original?: SubscriptionApi
	) => UpdateSubscriptionApi;
	handleChangePlan: (familyId: string, planId: string) => void;
	rawSubscriptions: SubscriptionsApi;
	setTermsNamesToAccept: React.Dispatch<React.SetStateAction<string[]>>;
	termsNamesToAccept: string[];
	estimating: boolean;
}

const SubscriptionConfigForm = (props: Props) => {
	const { setValue, watch, control } = props.form;
	const [showCancelSubscriptionModal, setShowCancelSubscriptionModal] = useState<boolean>(false);
	const watchedPlan = watch(`subscriptions.${props.index}.plan_id`);
	const watchedStatus = watch(`subscriptions.${props.index}.status`);
	const plan = props.config?.plans.find((f) => f.plan_id === watchedPlan);
	const { replace } = useFieldArray({
		control,
		name: `subscriptions.${props.index}.addons`,
	});

	const onResumeSubscription = () => {
		const rawSubscription = props.rawSubscriptions.subscriptions.find(
			({ plan_id }) => plan_id === props.updateSubscription.plan_id
		);
		const originalSubscription = props.createDefaultSubscription(props.config, props.original);
		const resumeSubscription = !isArchivePlan(props.config, props.updateSubscription.plan_id)
			? props.updateSubscription
			: originalSubscription;
		resumeSubscription.status = rawSubscription?.status === "IN_TRIAL" ? "IN_TRIAL" : "ACTIVE";
		resumeSubscription.plan_id = originalSubscription.plan_id;
		props.handleUpdateSubscription(props.index, resumeSubscription);
		replace(resumeSubscription.addons);
	};

	const handleChangePlan = (familyId: string, planId: string) => {
		props.handleChangePlan(familyId, planId);
	};

	const onCancelSubscription = () => {
		setShowCancelSubscriptionModal(true);
	};

	const onRemoveSubscription = () => {
		props.handleRemoveSubscription(props.index);
	};

	const onHideCancelSubscriptionModal = (data?: CancelSubscriptionApi) => {
		setShowCancelSubscriptionModal(false);
		if (data) {
			setValue(`subscriptions.${props.index}.cancellation_reason`, data.reason);
			setValue(`subscriptions.${props.index}.reason_details`, data.reason_details);
			setValue(`subscriptions.${props.index}.status`, "CANCELLED");
			setValue(`subscriptions.${props.index}.scheduled_cancellation_at`, data.cancel_date);
		}
	};

	if (props.updateSubscription) {
		const familyId = props.updateSubscription.family_id;
		if (!props.config) return null;

		if (props.estimate && props.estimate.edit_mode === "READ_ONLY") {
			return null;
		}

		switch (watchedStatus) {
			case "CANCELLED":
			case "NON_RENEWING":
				return (
					<CancelledSubscription
						form={props.form}
						index={props.index}
						onResume={onResumeSubscription}
						editable={true}
						config={props.config}
						original={props.original}
						plan={plan}
					/>
				);
			case "PAUSED":
				return (
					<PausedSubscription
						onResume={onResumeSubscription}
						estimate={props.estimate}
						config={props.config}
						plan={plan}
						form={props.form}
						index={props.index}
						editable={!!plan?.edit_mode}
					/>
				);
			case "ACTIVE":
			case "IN_TRIAL":
				return (
					<>
						{showCancelSubscriptionModal && (
							<CancelSubscriptionModal
								handleAction={(data) => onHideCancelSubscriptionModal(data)}
								familyId={familyId}
								onHide={() => onHideCancelSubscriptionModal()}
								cancelDate={
									props.original.current_term_end
										? props.original.current_term_end
										: props.original.trial_end
										? props.original.trial_end
										: props.original.cancel_date
								}
							/>
						)}
						<ActiveSubscriptionForm
							handleRemove={onRemoveSubscription}
							handleCancel={onCancelSubscription}
							handleResume={onResumeSubscription}
							handleChangePlan={handleChangePlan}
							estimate={props.estimate}
							updateSubscription={props.updateSubscription}
							key={`${familyId}_${props.index}`}
							index={props.index}
							form={props.form}
							config={props.config}
							original={props.original}
							setTermsNamesToAccept={props.setTermsNamesToAccept}
							termsNamesToAccept={props.termsNamesToAccept}
							estimating={props.estimating}
						/>
					</>
				);
			default:
				break;
		}
	}
	return null;
};
export default SubscriptionConfigForm;
